export const SOCIAL_PLATFORMS = [
  {
    id: 'x',
    label: 'X',
    placeholder: 'username',
    prefix: 'https://x.com/',
  },
  {
    id: 'discord',
    label: 'Discord',
    placeholder: 'username or invite URL',
    prefix: '',
  },
  {
    id: 'metafy',
    label: 'Metafy',
    placeholder: 'coach-name',
    prefix: 'https://metafy.gg/@',
  },
  {
    id: 'youtube',
    label: 'YouTube',
    placeholder: '@channel or channel URL',
    prefix: 'https://youtube.com/',
  },
  {
    id: 'twitch',
    label: 'Twitch',
    placeholder: 'username',
    prefix: 'https://twitch.tv/',
  },
  {
    id: 'website',
    label: 'Website',
    placeholder: 'https://example.com',
    prefix: '',
  },
] as const;

export type SocialPlatformId = typeof SOCIAL_PLATFORMS[number]['id'];
export type SocialLinks = Partial<Record<SocialPlatformId, string>>;

const SOCIAL_IDS = new Set<string>(SOCIAL_PLATFORMS.map((platform) => platform.id));

export function normalizeSocialLinks(value: unknown): SocialLinks {
  if (!value || typeof value !== 'object') return {};

  const links: SocialLinks = {};
  for (const [key, raw] of Object.entries(value)) {
    if (!SOCIAL_IDS.has(key) || typeof raw !== 'string') continue;
    const trimmed = raw.trim().slice(0, 120);
    if (trimmed) links[key as SocialPlatformId] = trimmed;
  }
  return links;
}

export function buildSocialUrl(platformId: SocialPlatformId, value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (platformId === 'website') return `https://${trimmed}`;
  const platform = SOCIAL_PLATFORMS.find((item) => item.id === platformId);
  if (!platform?.prefix) return null;
  const normalized = platformId === 'youtube' ? trimmed : trimmed.replace(/^@/, '');
  return `${platform.prefix}${normalized}`;
}

export function getDisplayHandle(platformId: SocialPlatformId, value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (platformId === 'website') return url.hostname.replace(/^www\./, '');
      const segments = url.pathname.split('/').filter(Boolean);
      const last = segments[segments.length - 1] ?? '';
      if (!last) return trimmed;
      if (platformId === 'discord') return last;
      return last.startsWith('@') ? last : `@${last}`;
    } catch {
      return platformId === 'website' ? trimmed : trimmed;
    }
  }

  if (platformId === 'website') return trimmed.replace(/^www\./, '');
  if (platformId === 'discord') return trimmed;
  return trimmed.startsWith('@') ? trimmed : `@${trimmed}`;
}
